import { useCallback } from "react"
import { ConnectionHandler, graphql, useMutation, UseMutationConfig } from "react-relay"
import { format } from "date-fns"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import {
  CarePlanPatientInput,
  CarePlanTreatmentPlanInput,
} from "@/modules/patients/services/ch/__generated__/chSaveCarePlanDataMutation.graphql"
import { useChUpdateProgressNoteFromTimeWindowMutation as TMutation } from "@/modules/patients/services/ch/__generated__/useChUpdateProgressNoteFromTimeWindowMutation.graphql"
import { useChUpdateTimeWindowMutation$data } from "@/modules/patients/services/ch/__generated__/useChUpdateTimeWindowMutation.graphql"
import { useApproveAndGenerateCarePlanDocumentMutation } from "@/modules/patients/services/useApproveAndGenerateCarePlanDocument"
import { useSaveCarePlanDataMutation } from "@/modules/patients/services/useSaveCarePlanDataMutation"

const updateProgressNoteMutation = graphql`
  mutation useChUpdateProgressNoteFromTimeWindowMutation(
    $input: Ch_updateProgressNotePointOfCareFromTimeWindowInput!
    $connections: [ID!]!
  ) {
    ch_updateProgressNotePointOfCareFromTimeWindow(input: $input) {
      ch_ProgressNote @appendNode(connections: $connections, edgeTypeName: "ch_ProgressNoteEdge") {
        id
        episodeOfCareId
        appointment {
          id
          language
        }
        pointOfCare {
          careProvider {
            id
          }
        }
        treatmentPlan {
          title
          followUpInstructions
          recommended
          from
          to
        }
      }
      errors {
        ... on ProgressNoteNotFound {
          message
        }
      }
    }
  }
`

type CommitConfig = Omit<UseMutationConfig<TMutation>, "variables"> & {
  timeWindow: NonNullable<useChUpdateTimeWindowMutation$data["ch_updateTimeWindow"]["ch_TimeWindow"]>
  patient?: PatientFragment$data
}

export const useChUpdateProgressNoteFromTimeWindow = () => {
  const patientId = usePatientParam()

  const generateCarePlan = useSaveCarePlanDataMutation()
  const approveAndGenerateCarePlanDocumentMutation = useApproveAndGenerateCarePlanDocumentMutation()
  const [commit, isFlight] = useMutation<TMutation>(updateProgressNoteMutation)

  const commitHandler = useCallback(
    (config: CommitConfig) => {
      const { timeWindow, patient, ...rest } = config
      const progressNoteId = timeWindow?.progressNoteId
      const episodeOfCareId = timeWindow?.progressNote?.episodeOfCareId ?? ""
      if (!progressNoteId) return
      const connectionID = ConnectionHandler.getConnectionID(
        episodeOfCareId,
        "EpisodeOfCareProgressNotesConnection_progressNotes",
      )
      const previousFrom = timeWindow.progressNote?.treatmentPlan?.from
      const previousTo = timeWindow.progressNote?.treatmentPlan?.to
      commit({
        ...rest,
        variables: {
          input: {
            dateFrom: format(timeWindow.dateFrom, "yyyy-MM-dd"),
            dateTo: format(timeWindow.dateTo, "yyyy-MM-dd"),
            careProviderId: timeWindow.referralToCareProviderId ? timeWindow.referralToCareProviderId : null,
            id: progressNoteId,
          },
          connections: [connectionID],
        },
        onCompleted: (response: TMutation["response"], errors) => {
          rest?.onCompleted?.(response, errors)
          const progressNote = response.ch_updateProgressNotePointOfCareFromTimeWindow.ch_ProgressNote
          if (!progressNote?.treatmentPlan) return
          const { treatmentPlan, appointment } = progressNote
          if (previousFrom === treatmentPlan.from && previousTo === treatmentPlan.to) {
            // This means that we don't need to update the treatment document.
            return
          }
          const patientData: CarePlanPatientInput = {
            firstName: patient?.firstName ?? "",
            lastName: patient?.lastName ?? "",
            birthDate: patient?.birthDate ?? "",
          }
          const treatPlan: CarePlanTreatmentPlanInput = {
            title: treatmentPlan?.title,
            from: treatmentPlan?.from,
            to: treatmentPlan?.to,
            recommendedTreatment: treatmentPlan?.recommended ?? "",
            followUpTreatment: treatmentPlan?.followUpInstructions ?? "",
          }
          generateCarePlan(
            {
              progressNoteId: progressNoteId,
              patientId,
              patient: patientData,
              appointmentId: appointment.id,
              appointmentLanguage: appointment.language,
              treatmentPlan: treatPlan,
              medications: [],
              therapies: [],
              recommendedMedications: [],
              keepOriginalAuthor: false,
            },
            async () => {
              await approveAndGenerateCarePlanDocumentMutation({ progressNoteId, keepOriginalAuthor: false })
            },
          )
        },
      })
    },
    [commit, generateCarePlan, approveAndGenerateCarePlanDocumentMutation, patientId],
  )

  return [commitHandler, isFlight] as const
}
